import { useEffect } from 'react';

const GAP = 10;

/** The part of the panel's map (its parent) inside the window, in window coordinates,
 *  its right edge short of the map's own buttons. */
function visibleMap(el) {
  const map = el.parentElement;
  const m = map.getBoundingClientRect();
  let right = Math.min(m.right, window.innerWidth);
  const controls = map.querySelector(':scope > .map-controls');
  const c = controls && controls.getBoundingClientRect();
  if (c && c.width) right = Math.min(right, c.left - GAP);
  return { map, m, left: Math.max(m.left, 0), top: Math.max(m.top, 0), right,
           bottom: Math.min(m.bottom, window.innerHeight) };
}

/** `pos` ({ x, y }: the panel's left and top, or with `centred` its offset from the
 *  middle of the map and its top) moved the least it takes for the panel `el` to lie
 *  wholly on the visible map; `pos` itself when it already does. A fixed panel
 *  (fullscreen) is placed in window coordinates, any other in the map's. */
export function onMap(el, pos, centred = false) {
  if (!el || !el.parentElement) return pos;
  const { map, m, left, top, right, bottom } = visibleMap(el);
  const fixed = getComputedStyle(el).position === 'fixed';
  const ox = fixed ? 0 : m.left + map.clientLeft;
  const oy = fixed ? 0 : m.top + map.clientTop;
  const shift = centred ? map.clientWidth / 2 - el.offsetWidth / 2 : 0;
  const x = Math.round(Math.max(left - ox, Math.min(pos.x + shift, right - ox - el.offsetWidth)) - shift);
  const y = Math.round(Math.max(top - oy, Math.min(pos.y, bottom - oy - el.offsetHeight)));
  return x === pos.x && y === pos.y ? pos : { x, y };
}

/** Keeps the panel in `ref` wholly on the map while `active`: when it grows where it
 *  was dropped (a scenario's figures, an opened list) and when the map or the window
 *  changes size. The panel gets the visible map's height as --map-visible-height, for
 *  a max-height that does not depend on where it is. */
export function useKeepOnMap(ref, setPos, active, centred = false) {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || !el.parentElement) return undefined;
    const fit = () => {
      const { top, bottom } = visibleMap(el);
      const height = `${Math.max(0, Math.round(bottom - top))}px`;
      if (el.style.getPropertyValue('--map-visible-height') !== height) {
        el.style.setProperty('--map-visible-height', height);
      }
      setPos((p) => onMap(el, p, centred));
    };
    fit();
    window.addEventListener('resize', fit);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(fit);
      ro.observe(el);
      ro.observe(el.parentElement);
    }
    return () => {
      window.removeEventListener('resize', fit);
      if (ro) ro.disconnect();
    };
  }, [ref, setPos, active, centred]);
}
